import { useEditor, EditorContent } from "@tiptap/react";
import BoldExtension from "@tiptap/extension-bold";
import BulletListExtension from "@tiptap/extension-bullet-list";
import OrderedListExtension from "@tiptap/extension-ordered-list";
import ListItemExtension from "@tiptap/extension-list-item";
import DocumentExtension from "@tiptap/extension-document";
import HardBreakExtension from "@tiptap/extension-hard-break";
import HistoryExtension from "@tiptap/extension-history";
import ItalicExtension from "@tiptap/extension-italic";
import LinkExtension from "@tiptap/extension-link";
import ParagraphExtension from "@tiptap/extension-paragraph";
import PlaceholderExtension from "@tiptap/extension-placeholder";
import HorizontalRuleExtension from "@tiptap/extension-horizontal-rule";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextExtension from "@tiptap/extension-text";
import React, { FC } from "react";
import { cn } from "@src/lib/utils/cn";
import useBrandColor from "@src/features/settings/hooks/useBrandColor";
import { getLinkExtensionHTMLStyles } from "../hooks/useRichTextEditorExtensions";
type Props = {
  value: string;
  className?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
};

const SimpleRichTextEditor: FC<Props> = ({
  value,
  className = "",
  placeholder = "This module will teach you...",
  onChange,
}) => {
  const brandColor = useBrandColor();
  const editor = useEditor({
    extensions: [
      TextExtension,
      ParagraphExtension,
      DocumentExtension,
      BoldExtension,
      ItalicExtension,
      HardBreakExtension,
      HorizontalRuleExtension,
      Table.configure({
        HTMLAttributes: {
          class: "border-collapse border rounded-xl w-full",
          style: { borderColor: "var(--gray-5-alpha)" },
        },
      }),
      TableRow.configure(),
      TableHeader.configure(),
      TableCell.configure({
        HTMLAttributes: {
          class:
            "break-words min-w-[60px] min-h-[60px] p-1 text-sm leading-[18px]",
          style: { borderColor: "var(--gray-5-alpha)" },
        },
      }),
      OrderedListExtension.configure({
        HTMLAttributes: {
          class: "list-decimal",
        },
      }),
      ListItemExtension.configure({
        HTMLAttributes: {
          class: undefined,
        },
      }),
      BulletListExtension.configure({
        HTMLAttributes: {
          class: "list-disc",
        },
      }),
      HistoryExtension,
      LinkExtension.extend({ inclusive: true }).configure({
        openOnClick: false,
        HTMLAttributes: {
          style: getLinkExtensionHTMLStyles(brandColor),
        },
      }),
      PlaceholderExtension.configure({
        placeholder,
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: cn(
          "min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-[14px] tracking-[-0.006em]",
          "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "max-h-[300px] overflow-y-auto", // Add this line for scrolling
          className,
        ),
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });
  return <EditorContent editor={editor} placeholder={placeholder} />;
};

export default SimpleRichTextEditor;
